import { useEffect, useState } from "react";
import { api, apiError } from "../lib/api";
import { toast } from "sonner";
import { Plus, X, Shirt, ChevronRight, CheckCircle2, XCircle, Clock, Link2, FlaskConical, Search, Trash2 } from "lucide-react";

const MATERIAL_TYPES = ["Fabric", "Lining", "Interlining", "Trim", "Accessory", "Packaging"];
const LIFECYCLE_STATUSES = ["Development", "Sampling", "Approved", "Restricted", "Discontinued"];

const STATUS_STYLE = {
  draft: "bg-zinc-100 text-zinc-700",
  submitted: "bg-amber-100 text-amber-800",
  approved: "bg-emerald-100 text-emerald-800",
  rejected: "bg-red-100 text-red-700",
};

export default function MaterialManagement() {
  const [materials, setMaterials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ name: "", material_type: "Fabric", lifecycle_status: "Development", construction_type: "", fibre_composition: "", weight_gsm: "", width_cm: "", supplier_id: "" });
  const [saving, setSaving] = useState(false);
  const [suppliers, setSuppliers] = useState([]);
  const [supplierLinks, setSupplierLinks] = useState([]);
  const [testResults, setTestResults] = useState([]);
  const [showLinkSupplier, setShowLinkSupplier] = useState(false);
  const [linkForm, setLinkForm] = useState({ supplier_id: "", moq: "", unit_price: "", lead_time_days: "", currency: "USD" });
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("");

  const load = () => {
    const params = new URLSearchParams();
    if (searchTerm) params.set("search", searchTerm);
    if (statusFilter) params.set("status", statusFilter);
    const qs = params.toString();
    api.get(`/material-masters${qs ? `?${qs}` : ""}`)
      .then((r) => setMaterials(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  };
  useEffect(() => {
    load();
    api.get("/suppliers").then((r) => setSuppliers(r.data)).catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [searchTerm, statusFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadDetail = async (m) => {
    setSelected(m);
    // Load supplier links for this material
    api.get(`/material-masters/${m.id}/supplier-links`)
      .then((r) => setSupplierLinks(r.data))
      .catch(() => setSupplierLinks([]));
    // Load test results linked to this material
    api.get(`/test-results?material_master_id=${m.id}`)
      .then((r) => setTestResults(r.data))
      .catch(() => setTestResults([]));
  };

  const create = async () => {
    setSaving(true);
    try {
      const payload = {
        name: form.name,
        material_type: form.material_type,
        lifecycle_status: form.lifecycle_status,
        construction_type: form.construction_type,
        weight_gsm: form.weight_gsm ? parseFloat(form.weight_gsm) : null,
        width_cm: form.width_cm ? parseFloat(form.width_cm) : null,
      };
      if (form.fibre_composition) {
        try {
          payload.fibre_composition = JSON.parse(form.fibre_composition);
        } catch {
          payload.fibre_composition = [{ fibre: form.fibre_composition, percentage: 100 }];
        }
      }
      await api.post("/material-masters", payload);
      toast.success("Material created");
      setShowCreate(false);
      setForm({ name: "", construction_type: "", fibre_composition: "", weight_gsm: "", width_cm: "", supplier_id: "" });
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/material-masters/${id}/status`, { status });
      toast.success(`Material ${status}`);
      setSelected(null);
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const addSupplierLink = async () => {
    if (!selected) return;
    try {
      await api.post(`/material-masters/${selected.id}/supplier-links`, {
        supplier_id: linkForm.supplier_id,
        moq: linkForm.moq ? parseInt(linkForm.moq, 10) : null,
        unit_price: linkForm.unit_price ? parseFloat(linkForm.unit_price) : null,
        lead_time_days: linkForm.lead_time_days ? parseInt(linkForm.lead_time_days, 10) : null,
        currency: linkForm.currency,
      });
      toast.success("Supplier linked");
      setShowLinkSupplier(false);
      setLinkForm({ supplier_id: "", moq: "", unit_price: "", lead_time_days: "", currency: "USD" });
      loadDetail(selected);
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const supplierName = (id) => {
    const s = suppliers.find((s) => s.id === id);
    return s ? (s.company_name || s.name) : id?.slice(0, 8) || "-";
  };

  return (
    <div className="p-10 max-w-6xl mx-auto">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-xs uppercase tracking-[0.25em] text-zinc-400">PLM</p>
          <h1 className="font-heading text-3xl font-bold mt-2 tracking-tight">Materials</h1>
          <p className="text-zinc-500 mt-2 text-sm">Material master list with fibre composition, suppliers and approval workflow.</p>
        </div>
        <button onClick={() => setShowCreate(true)}
          className="inline-flex items-center gap-2 bg-zinc-950 text-white px-5 py-2.5 rounded-md text-sm font-medium hover:bg-zinc-800 transition-colors">
          <Plus size={15} /> New material
        </button>
      </div>

      {loading ? (
        <div className="mt-10 text-sm text-zinc-400">Loading...</div>
      ) : materials.length === 0 ? (
        <div className="mt-10 border-2 border-dashed border-zinc-200 rounded-lg p-16 text-center text-zinc-400">
          <Shirt size={26} className="mx-auto mb-3" />
          <p className="text-sm">No materials yet.</p>
        </div>
      ) : (
        <>
        {/* Search & Filter */}
        <div className="mt-6 flex items-center gap-3">
          <div className="relative flex-1 max-w-xs">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
            <input value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} placeholder="Search materials..."
              className="w-full pl-9 pr-3 py-2 border border-zinc-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-zinc-950" />
          </div>
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
            className="border border-zinc-300 rounded-md px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-zinc-950">
            <option value="">All statuses</option>
            <option value="draft">Draft</option>
            <option value="submitted">Submitted</option>
            <option value="approved">Approved</option>
            <option value="rejected">Rejected</option>
          </select>
        </div>

        <div className="mt-4 flex gap-6">
          {/* Material List */}
          <div className="flex-1 border border-zinc-200 bg-white rounded-lg overflow-hidden">
            <div className="grid grid-cols-12 px-6 py-3 border-b border-zinc-100 text-[11px] uppercase tracking-[0.15em] text-zinc-400">
              <span className="col-span-3">Name</span>
              <span className="col-span-2">Construction</span>
              <span className="col-span-3">Composition</span>
              <span className="col-span-2">Status</span>
              <span className="col-span-2 text-right">Details</span>
            </div>
            {materials.map((m) => {
              const compDisplay = Array.isArray(m.fibre_composition)
                ? m.fibre_composition.map((f) => `${f.percentage || ""}% ${f.fibre || ""}`).join(", ")
                : m.fibre_composition || m.composition || "";
              return (
              <div key={m.id} className={`grid grid-cols-12 items-center px-6 py-4 border-b border-zinc-50 cursor-pointer hover:bg-zinc-50 ${selected?.id === m.id ? "bg-zinc-50" : ""}`}
                onClick={() => loadDetail(m)}>
                <span className="col-span-3 text-sm font-medium">{m.name}</span>
                <span className="col-span-2 text-sm text-zinc-600">{m.construction_type || m.material_type || ""}</span>
                <span className="col-span-3 text-sm text-zinc-600 truncate">{compDisplay}</span>
                <span className="col-span-2">
                  <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${STATUS_STYLE[m.status] || STATUS_STYLE.draft}`}>{m.status}</span>
                </span>
                <span className="col-span-2 text-right">
                  <ChevronRight size={14} className="inline text-zinc-400" />
                </span>
              </div>
              );
            })}
          </div>

          {/* Detail Panel */}
          {selected && (
            <div className="w-96 shrink-0 border border-zinc-200 bg-white rounded-lg p-6">
              <h3 className="font-heading font-semibold">{selected.name}</h3>
              <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${STATUS_STYLE[selected.status] || STATUS_STYLE.draft}`}>{selected.status}</span>
              <div className="mt-4 space-y-3">
                <div>
                  <p className="text-[10px] uppercase tracking-[0.2em] text-zinc-400">Construction Type</p>
                  <p className="text-sm">{selected.construction_type || selected.material_type || "-"}</p>
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-[0.2em] text-zinc-400">Fibre Composition</p>
                  <p className="text-sm">{Array.isArray(selected.fibre_composition) ? selected.fibre_composition.map((f) => `${f.percentage || ""}% ${f.fibre || ""}`).join(", ") : selected.fibre_composition || selected.composition || "-"}</p>
                </div>
                {(selected.weight_gsm || selected.weight) && (
                  <div>
                    <p className="text-[10px] uppercase tracking-[0.2em] text-zinc-400">Weight (GSM)</p>
                    <p className="text-sm">{selected.weight_gsm || selected.weight}</p>
                  </div>
                )}
                {selected.width_cm && (
                  <div>
                    <p className="text-[10px] uppercase tracking-[0.2em] text-zinc-400">Width (cm)</p>
                    <p className="text-sm">{selected.width_cm}</p>
                  </div>
                )}
                {selected.approved_season && (
                  <div>
                    <p className="text-[10px] uppercase tracking-[0.2em] text-zinc-400">Approved Season</p>
                    <p className="text-sm">{selected.approved_season}</p>
                  </div>
                )}
                {selected.approval_date && (
                  <div>
                    <p className="text-[10px] uppercase tracking-[0.2em] text-zinc-400">Approved</p>
                    <p className="text-sm text-zinc-600">{new Date(selected.approval_date).toLocaleDateString()}</p>
                  </div>
                )}
              </div>

              {/* Supplier Links */}
              <div className="mt-5 pt-4 border-t border-zinc-100">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-[10px] uppercase tracking-[0.2em] text-zinc-400 flex items-center gap-1"><Link2 size={10} /> Supplier Links</p>
                  <button onClick={() => setShowLinkSupplier(true)} className="text-[10px] text-zinc-500 hover:text-zinc-900">+ Link</button>
                </div>
                {supplierLinks.length === 0 ? (
                  <p className="text-xs text-zinc-400">No suppliers linked yet.</p>
                ) : (
                  <div className="space-y-2">
                    {supplierLinks.map((sl, i) => (
                      <div key={i} className="bg-zinc-50 rounded-md p-2.5">
                        <p className="text-xs font-medium">{supplierName(sl.supplier_id)}</p>
                        <div className="grid grid-cols-3 gap-2 mt-1 text-[10px] text-zinc-500">
                          {sl.moq != null && <div>MOQ: <span className="font-medium text-zinc-700">{sl.moq}</span></div>}
                          {sl.unit_price != null && <div>Price: <span className="font-medium text-zinc-700">{sl.currency || "USD"} {sl.unit_price}</span></div>}
                          {sl.lead_time_days != null && <div>Lead: <span className="font-medium text-zinc-700">{sl.lead_time_days}d</span></div>}
                        </div>
                        {sl.pricing_history && sl.pricing_history.length > 0 && (
                          <div className="mt-1.5 text-[10px] text-zinc-400">
                            Price history: {sl.pricing_history.map((p, j) => (
                              <span key={j} className="mr-1">{p.date?.slice(0, 7)}: {p.price}</span>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Test Results */}
              <div className="mt-4 pt-3 border-t border-zinc-100">
                <p className="text-[10px] uppercase tracking-[0.2em] text-zinc-400 flex items-center gap-1 mb-2"><FlaskConical size={10} /> Test Results</p>
                {testResults.length === 0 ? (
                  <p className="text-xs text-zinc-400">No test results for this material.</p>
                ) : (
                  <div className="space-y-1.5">
                    {testResults.slice(0, 5).map((tr) => (
                      <div key={tr.id} className="flex items-center justify-between text-xs bg-zinc-50 rounded px-2 py-1.5">
                        <span className="font-medium">{tr.test_type || "Material Test"}</span>
                        <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${tr.result === "pass" ? "bg-emerald-100 text-emerald-800" : tr.result === "fail" ? "bg-red-100 text-red-700" : "bg-amber-100 text-amber-800"}`}>
                          {tr.result}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="mt-5 pt-4 border-t border-zinc-100 space-y-2">
                {(selected.status === "draft" || selected.status === "rejected") && (
                  <button onClick={() => updateStatus(selected.id, "submitted")}
                    className="w-full inline-flex items-center justify-center gap-1.5 bg-amber-50 text-amber-800 px-4 py-2 rounded-md text-sm font-medium hover:bg-amber-100">
                    <Clock size={14} /> Submit for approval
                  </button>
                )}
                {selected.status === "submitted" && (
                  <>
                    <button onClick={() => updateStatus(selected.id, "approved")}
                      className="w-full inline-flex items-center justify-center gap-1.5 bg-emerald-50 text-emerald-800 px-4 py-2 rounded-md text-sm font-medium hover:bg-emerald-100">
                      <CheckCircle2 size={14} /> Approve
                    </button>
                    <button onClick={() => updateStatus(selected.id, "rejected")}
                      className="w-full inline-flex items-center justify-center gap-1.5 bg-red-50 text-red-700 px-4 py-2 rounded-md text-sm font-medium hover:bg-red-100">
                      <XCircle size={14} /> Reject
                    </button>
                  </>
                )}
                {selected.status !== "approved" && (
                  <button onClick={async () => {
                    if (!window.confirm("Archive this material?")) return;
                    try {
                      await api.delete(`/material-masters/${selected.id}`);
                      toast.success("Material archived");
                      setSelected(null);
                      load();
                    } catch (e) { toast.error(apiError(e)); }
                  }}
                    className="w-full inline-flex items-center justify-center gap-1.5 bg-zinc-100 text-zinc-600 px-4 py-2 rounded-md text-sm font-medium hover:bg-zinc-200">
                    <Trash2 size={14} /> Archive
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
        </>
      )}

      {/* Link Supplier Modal */}
      {showLinkSupplier && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-6" onClick={() => setShowLinkSupplier(false)}>
          <div className="bg-white rounded-lg p-8 w-full max-w-sm" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-heading font-semibold text-lg">Link Supplier</h2>
              <button onClick={() => setShowLinkSupplier(false)} className="p-1.5 hover:bg-zinc-100 rounded-md"><X size={16} /></button>
            </div>
            <div className="space-y-3">
              <select value={linkForm.supplier_id} onChange={(e) => setLinkForm({ ...linkForm, supplier_id: e.target.value })}
                className="w-full border border-zinc-300 rounded-md px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-zinc-950">
                <option value="">Select supplier...</option>
                {suppliers.map((s) => <option key={s.id} value={s.id}>{s.company_name || s.name}</option>)}
              </select>
              <input type="number" value={linkForm.moq} onChange={(e) => setLinkForm({ ...linkForm, moq: e.target.value })} placeholder="MOQ (minimum order qty)"
                className="w-full border border-zinc-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-950" />
              <div className="grid grid-cols-2 gap-2">
                <input type="number" step="0.01" value={linkForm.unit_price} onChange={(e) => setLinkForm({ ...linkForm, unit_price: e.target.value })} placeholder="Unit price"
                  className="border border-zinc-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-950" />
                <select value={linkForm.currency} onChange={(e) => setLinkForm({ ...linkForm, currency: e.target.value })}
                  className="border border-zinc-300 rounded-md px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-zinc-950">
                  <option value="USD">USD</option>
                  <option value="EUR">EUR</option>
                  <option value="GBP">GBP</option>
                  <option value="CNY">CNY</option>
                </select>
              </div>
              <input type="number" value={linkForm.lead_time_days} onChange={(e) => setLinkForm({ ...linkForm, lead_time_days: e.target.value })} placeholder="Lead time (days)"
                className="w-full border border-zinc-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-950" />
              <button onClick={addSupplierLink} disabled={!linkForm.supplier_id}
                className="w-full bg-zinc-950 text-white py-2.5 rounded-md text-sm font-medium hover:bg-zinc-800 disabled:opacity-40">
                Link Supplier
              </button>
            </div>
          </div>
        </div>
      )}

      {showCreate && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-6" onClick={() => setShowCreate(false)}>
          <div className="bg-white rounded-lg p-8 w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-heading font-semibold text-lg">New material</h2>
              <button onClick={() => setShowCreate(false)} className="p-1.5 hover:bg-zinc-100 rounded-md"><X size={16} /></button>
            </div>
            <div className="space-y-4">
              {/* SOW Module 6 — material_type is the primary classification and drives how a
                  material is governed, costed and approved. It is a closed set, not free text. */}
              <select value={form.material_type} onChange={(e) => setForm({ ...form, material_type: e.target.value })}
                className="w-full border border-zinc-200 rounded-md px-3 py-2 text-sm">
                {MATERIAL_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
              <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Material name"
                className="w-full border border-zinc-300 rounded-md px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-950" />
              <input value={form.construction_type} onChange={(e) => setForm({ ...form, construction_type: e.target.value })} placeholder="Construction type (e.g. Woven, Knit, Non-woven)"
                className="w-full border border-zinc-300 rounded-md px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-950" />
              <input value={form.fibre_composition} onChange={(e) => setForm({ ...form, fibre_composition: e.target.value })} placeholder='Fibre composition (e.g. 100% Cotton or JSON array)'
                className="w-full border border-zinc-300 rounded-md px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-950" />
              <input value={form.weight_gsm} onChange={(e) => setForm({ ...form, weight_gsm: e.target.value })} placeholder="Weight GSM (optional)" type="number" step="0.1"
                className="w-full border border-zinc-300 rounded-md px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-950" />
              <input value={form.width_cm} onChange={(e) => setForm({ ...form, width_cm: e.target.value })} placeholder="Width cm (optional)" type="number" step="0.1"
                className="w-full border border-zinc-300 rounded-md px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-950" />
              {/* Lifecycle governs availability in the Design Studio picker — restricted and
                  discontinued materials are not selectable for new styles. */}
              <select value={form.lifecycle_status} onChange={(e) => setForm({ ...form, lifecycle_status: e.target.value })}
                className="w-full border border-zinc-200 rounded-md px-3 py-2 text-sm">
                {LIFECYCLE_STATUSES.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
              <button onClick={create} disabled={!form.name || !form.construction_type || saving}
                className="w-full bg-zinc-950 text-white py-3 rounded-md font-medium hover:bg-zinc-800 transition-colors disabled:opacity-40">
                {saving ? "Creating..." : "Create material"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
